import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { apiFetch } from "../lib/apiFetch";
import { RichText } from "../lib/richText";
import JobNoteCard from "./JobNoteCard";

const ADMIN_EMAILS = ["dylan@browning-electric.ca", "cklein@browning-electric.ca"];

// ─── Check TTS support ─────────────────────────────────────────────────────
function getTTSSupported() {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

// ─── AI voices (OpenAI TTS via /api/claude synthesizeSpeech) ─────────────────
const OPENAI_VOICES = [
  { id: "onyx", label: "Onyx — deep & confident" },
  { id: "echo", label: "Echo — warm male" },
  { id: "ash", label: "Ash — calm male" },
  { id: "verse", label: "Verse — expressive male" },
  { id: "alloy", label: "Alloy — neutral" },
  { id: "fable", label: "Fable — British" },
  { id: "ballad", label: "Ballad — smooth" },
  { id: "nova", label: "Nova — bright female" },
  { id: "coral", label: "Coral — friendly female" },
  { id: "shimmer", label: "Shimmer — soft female" },
  { id: "sage", label: "Sage — mellow female" },
];
const DEFAULT_VOICE = "openai:onyx";

// ─── Split long text into TTS-sized chunks on sentence boundaries ───────────
// OpenAI TTS caps input at 4096 chars; keep chunks well under so the base64
// mp3 responses stay small and playback starts fast.
function splitForTTS(text, maxLen = 2500) {
  const out = [];
  let remaining = text;
  while (remaining.length > maxLen) {
    let cut = remaining.lastIndexOf(". ", maxLen);
    if (cut < maxLen * 0.5) cut = remaining.lastIndexOf(" ", maxLen);
    if (cut <= 0) cut = maxLen;
    out.push(remaining.slice(0, cut + 1).trim());
    remaining = remaining.slice(cut + 1);
  }
  if (remaining.trim()) out.push(remaining.trim());
  return out;
}

// ─── Strip markdown for TTS ─────────────────────────────────────────────────
function stripMarkdownForTTS(text) {
  if (!text) return "";
  return text
    .replace(/```[\s\S]*?```/g, "")          // code blocks
    .replace(/`([^`]+)`/g, "$1")              // inline code
    .replace(/#{1,6}\s+/g, "")                // headings
    .replace(/\*\*(.+?)\*\*/g, "$1")          // bold
    .replace(/__(.+?)__/g, "$1")              // bold alt
    .replace(/\*(.+?)\*/g, "$1")              // italic
    .replace(/_(.+?)_/g, "$1")                // italic alt
    .replace(/^\s*[-*]\s+/gm, "")             // bullets
    .replace(/^\s*\d+\.\s+/gm, "")           // numbered lists
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")  // links
    .replace(/<autofill>[\s\S]*?<\/autofill>/g, "") // autofill blocks
    .replace(/\n{2,}/g, ". ")                 // paragraph breaks → pause
    .replace(/\n/g, " ")
    .trim();
}

// ─── Parse autofill block from response ──────────────────────────────────────
function parseAutoFill(text) {
  const match = text.match(/<autofill>([\s\S]*?)<\/autofill>/);
  if (!match) return null;
  try {
    return JSON.parse(match[1].trim());
  } catch {
    return null;
  }
}

function stripAutoFill(text) {
  return text.replace(/<autofill>[\s\S]*?<\/autofill>/, "").trim();
}

// ─── Markdown renderer ──────────────────────────────────────────────────────
// Delegates to the shared <RichText> (src/lib/richText.jsx). The renderer that
// used to live here handled bold/code/lists but had NO table support, so every
// comparison table Jai-Man wrote arrived as literal "| Option | Cost |" pipes,
// separator row and all. The same component now renders the scope chat and the
// quote refine chat, so all three read the same.
function renderMarkdown(text) {
  if (!text) return null;
  return <RichText text={text} accent="#f59e0b" />;
}

// ─── Detect if escalation is offered in a response ──────────────────────────
function hasEscalationOffer(text) {
  if (!text) return false;
  const lower = text.toLowerCase();
  return lower.includes("escalate this to dispatch") ||
    lower.includes("escalate to dispatch") ||
    lower.includes("escalation to dispatch");
}

function summarizeWriteActions(actions = []) {
  const writeActions = (Array.isArray(actions) ? actions : []).filter((a) =>
    ["createTask", "updateTask", "updateJobTitle", "updateJobCustomFields", "updateRequestNotes"].includes(a?.action)
  );
  if (!writeActions.length) return null;
  const okCount = writeActions.filter((a) => !!a?.ok).length;
  const failCount = writeActions.length - okCount;
  if (okCount > 0 && failCount === 0) {
    return { kind: "success", text: `Jobber update complete (${okCount} action${okCount > 1 ? "s" : ""}).` };
  }
  if (okCount > 0 && failCount > 0) {
    return { kind: "warn", text: `Jobber partial update: ${okCount} succeeded, ${failCount} failed.` };
  }
  return { kind: "error", text: `Jobber update failed (${failCount} action${failCount > 1 ? "s" : ""}).` };
}

// ─── Check SpeechRecognition support (fallback only) ────────────────────────
function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

// ─── Check MediaRecorder support ────────────────────────────────────────────
function getMediaRecorderSupported() {
  return typeof window !== "undefined" && typeof MediaRecorder !== "undefined";
}

// ─── Get best supported audio MIME type ─────────────────────────────────────
function getAudioMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) return "audio/ogg;codecs=opus";
  return null; // will use default
}

// ─── Convert Blob to base64 ─────────────────────────────────────────────────
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ─── Format relative time ───────────────────────────────────────────────────
function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

function formatTimestamp(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
}

function formatJobberActionLabel(action = "") {
  const map = {
    getSchedule: "Live Schedule",
    searchJobs: "Live Jobs",
    listTasks: "Live Tasks",
    searchTasks: "Live Task Search",
    createTask: "Task Created",
    updateTask: "Task Updated",
    updateJobTitle: "Job Updated",
    updateJobCustomFields: "Job Fields Updated",
    updateRequestNotes: "Request Updated",
  };
  return map[action] || action;
}

// ─── Job-note cards: what became of each one ─────────────────────────────────
// A drafted job note is added by a TAP on its card, which the model never sees:
// only the text of earlier turns is sent back. Without a status line Jai-Man
// has to guess whether the draft is still waiting, and "also add 20 feet of
// 14/2" gets a second full note: the same stock on the job twice. So one line
// per card is written under the reply that drafted it, in the text that is
// SENT only. The person never sees these lines.

// A later reply retires an earlier card that nobody has acted on when it drafts
// the same job again (a correction), or when it says it replaces that job
// number (the person said the number was wrong).
function replacedByLaterDraft(messages, i, d) {
  const n = Number(d.job?.number);
  return messages.slice(i + 1).some((later) => (later.noteDrafts || []).some((x) => (
    Number(x.job?.number) === n || (x.replacesJobNumber != null && Number(x.replacesJobNumber) === n)
  )));
}

// Where the card for draft d (under message i) stands right now.
// "pending" is the only one that still has a live Add button.
function noteCardStatus(messages, i, d, now = Date.now()) {
  const outcome = (messages[i].noteOutcomes || {})[d.draftId] || {};
  const state = outcome.state;
  if (["posted", "queued", "posting", "unsure", "cancelled", "failed", "expired", "refused"].includes(state)) return state;
  // attempted = Add was tapped on that card and it did not go through. The
  // newer card may have been written while it was being added, and then holds
  // only what was said after it.
  if (replacedByLaterDraft(messages, i, d)) return outcome.attempted ? "tried_replaced" : "replaced";
  if (d.expiresAt && now > new Date(d.expiresAt).getTime()) return "old";
  return "pending";
}

function noteStatusLine(status, n) {
  switch (status) {
    case "posted": return `[Job note status: the tech tapped Add, and this note is ON job #${n} in Jobber. It cannot be changed and the stock on it is already counted. If they add or correct anything for job #${n}, draft ONLY what is new.]`;
    case "queued": return `[Job note status: the tech tapped Add for job #${n}. The note is saved and waiting to be sent to Jobber; it is not confirmed on the job yet. Treat its stock as already counted: draft ONLY what is new for job #${n}.]`;
    // The tap is on its way to the server and the answer is not back yet.
    case "posting": return `[Job note status: the tech tapped Add for job #${n} and it is being added right now; it is not confirmed on the job yet. Treat its stock as already counted: draft ONLY what is new for job #${n}.]`;
    // Add went out and no answer came back; the card is asking the server.
    case "unsure": return `[Job note status: the tech tapped Add for job #${n} but no answer came back. The note may or may not be on the job, and the card is checking. Do not draft this note again. If they add something, draft ONLY what is new for job #${n}.]`;
    case "tried_replaced": return `[Job note status: the tech tapped Add on the card for job #${n} but it did NOT go through, and a newer card has since replaced it. Nothing from that card is on the job. If the newer card only has part of the note, draft the full note again.]`;
    case "cancelled": return `[Job note status: the tech cancelled the draft for job #${n}. Nothing was added.]`;
    case "replaced": return `[Job note status: the card for job #${n} was replaced by a newer card. Nothing was added from it.]`;
    case "pending": return `[Job note status: the card for job #${n} is on their screen and has NOT been added yet.]`;
    // failed, expired, refused, and a card left untouched past its 30 minutes
    default: return `[Job note status: the draft for job #${n} was NOT added.]`;
  }
}

function withNoteStatus(m, i, messages, now = Date.now()) {
  if (m.role !== "assistant" || !Array.isArray(m.noteDrafts) || !m.noteDrafts.length) return m.content;
  const lines = m.noteDrafts.map((d) => noteStatusLine(noteCardStatus(messages, i, d, now), d.job?.number));
  return `${m.content}\n\n${lines.join("\n")}`;
}

// Is at least one card still waiting for its tap? The server uses this so "ok"
// is answered by Jai-Man ("it is not added until you tap the card") and never
// by the canned "Cool.".
function anyNoteDraftPending(messages, now = Date.now()) {
  return messages.some((m, i) => m.role === "assistant" && (m.noteDrafts || []).some((d) => noteCardStatus(messages, i, d, now) === "pending"));
}

// Is a tap on Add still on its way to the server?
function anyNoteBeingAdded(messages) {
  return messages.some((m) => m.role === "assistant" && (m.noteDrafts || []).some((d) => ((m.noteOutcomes || {})[d.draftId] || {}).state === "posting"));
}
const NOTE_ADD_WAIT_MS = 15000;

// ─── Main Widget ──────────────────────────────────────────────────────────────
export default function JManWidget({
  currentPath = "",
  assessmentData = {},
  jobContext = {},
  onAutoFill,
  userEmail = "",
  readOnly = false,
}) {
  const resolvedCurrentPath = currentPath || (typeof window !== "undefined" ? window.location.pathname : "/");
  const resolvedAssessmentData = useMemo(
    () => ({ ...(jobContext || {}), ...(assessmentData || {}) }),
    [jobContext, assessmentData]
  );
  const isAdmin = ADMIN_EMAILS.includes((userEmail || "").toLowerCase());
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hey, Jai-Man here. I'm plugged into your whole system — invoices, clients, materials, P&L, Jobber, everything. Ask me anything or give me your walking notes.",
      timestamp: new Date().toISOString(),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [fillBanner, setFillBanner] = useState(null);
  const [escalating, setEscalating] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [speechSupported] = useState(() => !!getSpeechRecognition());
  const [mediaRecSupported] = useState(() => getMediaRecorderSupported());
  const [transcribing, setTranscribing] = useState(false);
  const [whisperAvailable, setWhisperAvailable] = useState(null); // null = unknown, true/false after first check

  // Conversation memory state
  const [conversationId, setConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState(null);

  // History panel state
  const [showHistory, setShowHistory] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");

  const [showMemories, setShowMemories] = useState(false);
  const [memoryList, setMemoryList] = useState([]);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [memorySearch, setMemorySearch] = useState("");
  const [editingMemoryId, setEditingMemoryId] = useState(null);
  const [memoryDrafts, setMemoryDrafts] = useState({});
  const [savingMemoryId, setSavingMemoryId] = useState(null);
  const [copiedMessageKey, setCopiedMessageKey] = useState(null);
  const [writeStatusBanner, setWriteStatusBanner] = useState(null);

  // Voice recording timer
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [interimTranscript, setInterimTranscript] = useState("");

  // ── TTS / Voice Conversation state ──
  const [ttsSupported] = useState(() => getTTSSupported());
  const [voiceMode, setVoiceMode] = useState(() => {
    try { return localStorage.getItem("jm-voice-mode") === "true"; } catch { return false; }
  });
  const [autoListen, setAutoListen] = useState(() => {
    try { const v = localStorage.getItem("jm-auto-listen"); return v === null ? true : v === "true"; } catch { return true; }
  });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoListenTick, setAutoListenTick] = useState(0);
  const speakingMsgRef = useRef(null);
  const autoListenTriggerRef = useRef(false);

  // ── Voice selection (AI voices via backend TTS, device voices as fallback) ──
  const [selectedVoice, setSelectedVoice] = useState(() => {
    try { return localStorage.getItem("jm-voice") || DEFAULT_VOICE; } catch { return DEFAULT_VOICE; }
  });
  const [openaiTTSAvailable, setOpenaiTTSAvailable] = useState(null); // null = unknown, true/false after first synth
  const [browserVoices, setBrowserVoices] = useState([]);
  const audioRef = useRef(null);
  const speakSessionRef = useRef(0);

  // ── Hands-free voice conversation (GPT-style talk mode) ──
  // States: idle → listening (waiting for speech) → capturing (user talking) →
  // transcribing → thinking (waiting for reply) → speaking → listening → …
  const [voiceConvoActive, setVoiceConvoActive] = useState(false);
  const [convoStatus, setConvoStatus] = useState("idle");
  const convoActiveRef = useRef(false);
  const convoStatusRef = useRef("idle");
  const convoStreamRef = useRef(null);
  const convoAudioCtxRef = useRef(null);
  const convoAnalyserRef = useRef(null);
  const convoVadTimerRef = useRef(null);
  const convoRecorderRef = useRef(null);
  const convoChunksRef = useRef([]);
  const convoSpeechStartRef = useRef(0);
  const convoLastVoiceRef = useRef(0);
  const convoNoiseFloorRef = useRef(0.01);
  const convoBargeMsRef = useRef(0);
  // Timer/recorder callbacks route through refs so they always run the latest
  // render's logic (a setInterval closure would go stale otherwise)
  const convoTickRef = useRef(() => {});
  const convoCaptureStopRef = useRef(() => {});
  const messagesRef = useRef([]);

  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  const MAX_RECORDING_SECONDS = 300; // 5 minutes

  // Another page can hand Jai-Man a question (the SOP page's "Ask Jai-Man"
  // button): open the chat with it typed in, ready to send or change. It is
  // never sent for them — the person presses send.
  useEffect(() => {
    const onAsk = (e) => {
      const text = String(e?.detail?.text || "").trim();
      if (!text) return;
      setShowHistory(false);
      setShowMemories(false);
      setInput(text);
      setOpen(true);
    };
    window.addEventListener("elecbid:ask-jaiman", onAsk);
    return () => window.removeEventListener("elecbid:ask-jaiman", onAsk);
  }, []);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && textareaRef.current && !showHistory) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open, showHistory, showMemories]);

  // Clean up recording timer + media recorder on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(t => t.stop());
      }
      // Voice conversation teardown
      convoActiveRef.current = false;
      if (convoVadTimerRef.current) clearInterval(convoVadTimerRef.current);
      if (convoRecorderRef.current && convoRecorderRef.current.state !== "inactive") {
        convoRecorderRef.current.onstop = null;
        try { convoRecorderRef.current.stop(); } catch {}
      }
      if (convoStreamRef.current) {
        convoStreamRef.current.getTracks().forEach(t => t.stop());
      }
      if (convoAudioCtxRef.current) {
        try { convoAudioCtxRef.current.close(); } catch {}
      }
    };
  }, []);

  // ── TTS helpers ──
  const cancelSpeech = useCallback(() => {
    speakSessionRef.current += 1; // invalidate any in-flight AI TTS fetch/playback loop
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (ttsSupported) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    speakingMsgRef.current = null;
  }, [ttsSupported]);

  // Status lives in state (for UI) and a ref (for the VAD timer loop)
  const setConvoStatusBoth = useCallback((s) => {
    convoStatusRef.current = s;
    setConvoStatus(s);
  }, []);

  // Shared end-of-speech handler: clears speaking state + fires auto-listen
  const afterSpeechEnd = useCallback(() => {
    setIsSpeaking(false);
    speakingMsgRef.current = null;
    // Hands-free conversation: go straight back to listening for the next turn
    if (convoActiveRef.current) {
      setConvoStatusBoth("listening");
      return;
    }
    // Auto-listen: re-activate mic after speech ends (use ref to avoid circular dep)
    if (autoListen && voiceMode) {
      setTimeout(() => {
        const SpeechRec = getSpeechRecognition();
        if (SpeechRec && !recognitionRef.current?.running) {
          // Trigger mic via a custom event pattern — set a flag and let effect handle it
          autoListenTriggerRef.current = true;
          setAutoListenTick(t => t + 1);
        }
      }, 400);
    }
  }, [autoListen, voiceMode, setConvoStatusBoth]);

  // Device-voice path (Web Speech API) — used for "browser:" voices and as
  // fallback when the backend TTS is unavailable
  const speakWithBrowser = useCallback((clean, browserVoiceName) => {
    if (!ttsSupported) {
      afterSpeechEnd();
      return;
    }
    const utter = new SpeechSynthesisUtterance(clean);
    const voices = window.speechSynthesis.getVoices();
    const preferred =
      (browserVoiceName && voices.find(v => v.name === browserVoiceName)) ||
      voices.find(v => v.name.includes("Google UK English Male")) ||
      voices.find(v => v.name.includes("Microsoft David")) ||
      voices.find(v => v.name.includes("Male") && v.lang.startsWith("en")) ||
      voices.find(v => v.name.includes("Daniel") && v.lang.startsWith("en")) ||
      voices.find(v => v.lang === "en-US" && v.localService) ||
      voices.find(v => v.lang.startsWith("en")) ||
      voices[0];
    if (preferred) utter.voice = preferred;
    utter.rate = 0.95;
    utter.pitch = 1.0;

    utter.onstart = () => setIsSpeaking(true);
    utter.onend = () => afterSpeechEnd();
    utter.onerror = () => {
      if (convoActiveRef.current) {
        afterSpeechEnd(); // keep the hands-free loop going
      } else {
        setIsSpeaking(false);
        speakingMsgRef.current = null;
      }
    };

    window.speechSynthesis.speak(utter);
  }, [ttsSupported, afterSpeechEnd]);

  // Fetch one chunk of synthesized speech from the backend; null = unavailable
  const fetchTTSAudio = useCallback(async (text, voiceId) => {
    try {
      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "synthesizeSpeech", text, voice: voiceId }),
      });
      const data = await res.json();
      if (data.ttsAvailable === false) {
        setOpenaiTTSAvailable(false);
        return null;
      }
      if (!res.ok || !data.audioBase64) return null;
      setOpenaiTTSAvailable(true);
      return data.audioBase64;
    } catch (err) {
      console.warn("TTS fetch failed:", err);
      return null;
    }
  }, []);

  // Play one base64 mp3 chunk; resolves true when it finished playing
  const playAudioBase64 = useCallback((base64, session) => {
    return new Promise((resolve) => {
      if (speakSessionRef.current !== session) return resolve(false);
      const audio = new Audio(`data:audio/mpeg;base64,${base64}`);
      audioRef.current = audio;
      audio.onended = () => resolve(true);
      audio.onerror = () => resolve(false);
      audio.play().catch(() => resolve(false));
    });
  }, []);

  // AI-voice path: chunk long replies, prefetch the next chunk while the
  // current one plays so there's no gap between chunks
  const speakWithOpenAI = useCallback(async (clean, voiceId) => {
    const session = ++speakSessionRef.current;
    setIsSpeaking(true);
    const chunks = splitForTTS(clean);
    let nextPromise = fetchTTSAudio(chunks[0], voiceId);
    for (let i = 0; i < chunks.length; i++) {
      const b64 = await nextPromise;
      if (speakSessionRef.current !== session) return; // cancelled
      if (!b64) {
        // Backend TTS down — fall back to the device voice for the whole reply
        if (i === 0) {
          setIsSpeaking(false);
          speakWithBrowser(clean);
        } else {
          afterSpeechEnd();
        }
        return;
      }
      if (i + 1 < chunks.length) nextPromise = fetchTTSAudio(chunks[i + 1], voiceId);
      const played = await playAudioBase64(b64, session);
      if (speakSessionRef.current !== session) return; // cancelled mid-playback
      if (!played && i === 0) {
        // Playback blocked (e.g. autoplay policy) — try the device voice instead
        setIsSpeaking(false);
        speakWithBrowser(clean);
        return;
      }
    }
    audioRef.current = null;
    afterSpeechEnd();
  }, [fetchTTSAudio, playAudioBase64, speakWithBrowser, afterSpeechEnd]);

  const speakText = useCallback((text, { force = false } = {}) => {
    if (!force && !voiceMode) return;
    cancelSpeech();
    const clean = stripMarkdownForTTS(text);
    if (!clean) return;
    speakingMsgRef.current = text;

    const [kind, ...rest] = (selectedVoice || DEFAULT_VOICE).split(":");
    const voiceName = rest.join(":");
    if (kind === "openai" && openaiTTSAvailable !== false) {
      speakWithOpenAI(clean, voiceName || "onyx");
    } else {
      speakWithBrowser(clean, kind === "browser" ? voiceName : null);
    }
  }, [voiceMode, cancelSpeech, selectedVoice, openaiTTSAvailable, speakWithOpenAI, speakWithBrowser]);

  // Persist voice prefs
  useEffect(() => {
    try { localStorage.setItem("jm-voice-mode", voiceMode ? "true" : "false"); } catch {}
    if (!voiceMode) cancelSpeech();
  }, [voiceMode, cancelSpeech]);

  useEffect(() => {
    try { localStorage.setItem("jm-auto-listen", autoListen ? "true" : "false"); } catch {}
  }, [autoListen]);

  useEffect(() => {
    try { localStorage.setItem("jm-voice", selectedVoice); } catch {}
  }, [selectedVoice]);

  // Preload voices (Chrome loads them async) + keep the picker list in sync
  useEffect(() => {
    if (ttsSupported) {
      const loadVoices = () => {
        const vs = window.speechSynthesis.getVoices();
        setBrowserVoices(vs.filter(v => v.lang && v.lang.toLowerCase().startsWith("en")).slice(0, 20));
      };
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    return () => {
      if (ttsSupported) cancelSpeech();
    };
  }, [ttsSupported, cancelSpeech]);

  // ── Auto-listen trigger (avoids circular dep with toggleListening) ──
  // This is intentionally placed here; toggleListening is defined below.
  // The effect fires when speakText triggers autoListenTick increment.
  const autoListenEffectRef = useRef(null);
  useEffect(() => {
    if (autoListenTriggerRef.current && !listening) {
      autoListenTriggerRef.current = false;
      // Delay slightly to let state settle
      autoListenEffectRef.current = setTimeout(() => {
        if (!listening) toggleListening();
      }, 100);
    }
    return () => { if (autoListenEffectRef.current) clearTimeout(autoListenEffectRef.current); };
  }, [autoListenTick, listening]); // toggleListening intentionally omitted — defined below to avoid circular dep

  // ── Load conversation history ──
  const loadHistory = useCallback(async () => {
    if (!userEmail) return;
    setHistoryLoading(true);
    try {
      const action = historySearch.trim() ? "searchConversations" : "getConversationHistory";
      const body = {
        action,
        userEmail,
        ...(historySearch.trim() ? { query: historySearch.trim() } : {}),
      };
      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setHistoryList(data.conversations || []);
    } catch (err) {
      console.error("Failed to load history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, [userEmail, historySearch]);

  // Load history when panel opens
  useEffect(() => {
    if (showHistory) loadHistory();
  }, [showHistory, loadHistory]);

  const loadMemories = useCallback(async () => {
    if (!userEmail || !isAdmin) return;
    setMemoryLoading(true);
    try {
      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "getMemories",
          userEmail,
          query: memorySearch.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.details || "Failed to load memories");
      setMemoryList(data.memories || []);
    } catch (err) {
      console.error("Failed to load memories:", err);
    } finally {
      setMemoryLoading(false);
    }
  }, [userEmail, memorySearch, isAdmin]);

  useEffect(() => {
    if (showMemories) loadMemories();
  }, [showMemories, loadMemories]);

  // ── Load a past conversation into chat ──
  const loadConversation = async (convId) => {
    try {
      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "getConversation", conversationId: convId, userEmail }),
      });
      const data = await res.json();
      if (data.conversation) {
        const conv = data.conversation;
        const loadedMsgs = (conv.messages || []).map(m => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
        }));
        if (loadedMsgs.length > 0) {
          setMessages(loadedMsgs);
        }
        setConversationId(conv.id);
        setConversationTitle(conv.title || null);
        setShowHistory(false);
      }
    } catch (err) {
      console.error("Failed to load conversation:", err);
    }
  };

  // ── Start new chat ──
  const startNewChat = () => {
    setMessages([
      {
        role: "assistant",
        content: "Fresh chat started. What can I help with?",
        timestamp: new Date().toISOString(),
      },
    ]);
    setConversationId(null);
    setConversationTitle(null);
    setShowHistory(false);
    setShowMemories(false);
  };

  const startEditingMemory = (memory) => {
    setEditingMemoryId(memory.id);
    setMemoryDrafts((prev) => ({
      ...prev,
      [memory.id]: {
        subject_label: memory.subject_label || "",
        memory_text: memory.memory_text || "",
        tags: (memory.tags || []).join(", "),
        importance: String(memory.importance || 3),
        confidence: String(memory.confidence ?? ""),
      },
    }));
  };

  const cancelEditingMemory = () => {
    setEditingMemoryId(null);
  };

  const updateMemoryDraft = (memoryId, field, value) => {
    setMemoryDrafts((prev) => ({
      ...prev,
      [memoryId]: {
        ...(prev[memoryId] || {}),
        [field]: value,
      },
    }));
  };

  const saveMemory = async (memoryId, activeOverride) => {
    const draft = memoryDrafts[memoryId] || {};
    setSavingMemoryId(memoryId);
    try {
      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "updateMemory",
          userEmail,
          memoryId,
          updates: {
            subject_label: draft.subject_label,
            memory_text: draft.memory_text,
            tags: draft.tags,
            importance: draft.importance,
            confidence: draft.confidence,
            ...(activeOverride !== undefined ? { active: activeOverride } : {}),
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.details || "Failed to save memory");
      setMemoryList((prev) => prev.map((memory) => (memory.id === memoryId ? { ...memory, ...data.memory } : memory)));
      if (activeOverride === false) {
        setMemoryList((prev) => prev.filter((memory) => memory.id !== memoryId));
      }
      setEditingMemoryId(null);
    } catch (err) {
      console.error("Failed to update memory:", err);
      alert(`Could not update memory: ${err.message}`);
    } finally {
      setSavingMemoryId(null);
    }
  };

  // ── Helper: send audio to Whisper API for transcription ──
  const transcribeWithWhisper = useCallback(async (audioBlob, mimeType) => {
    setTranscribing(true);
    try {
      const audioBase64 = await blobToBase64(audioBlob);
      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "transcribeAudio",
          audioBase64,
          mimeType: mimeType || "audio/webm",
        }),
      });
      const data = await res.json();
      if (data.whisperAvailable === false) {
        setWhisperAvailable(false);
        return null; // signal to fall back
      }
      setWhisperAvailable(true);
      return data.transcript || "";
    } catch (err) {
      console.error("Whisper transcription failed:", err);
      return null;
    } finally {
      setTranscribing(false);
    }
  }, []);

  // ── Stop MediaRecorder and process audio ──
  const stopMediaRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
  }, []);

  // ── Start MediaRecorder recording ──
  const startMediaRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      const mimeType = getAudioMimeType();
      const options = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // Stop all tracks to release mic
        stream.getTracks().forEach(t => t.stop());
        audioStreamRef.current = null;

        const chunks = audioChunksRef.current;
        if (chunks.length === 0) {
          setListening(false);
          setRecordingSeconds(0);
          return;
        }

        const usedMime = recorder.mimeType || mimeType || "audio/webm";
        const audioBlob = new Blob(chunks, { type: usedMime });

        // Transcribe via Whisper
        const transcript = await transcribeWithWhisper(audioBlob, usedMime);
        if (transcript !== null && transcript.trim()) {
          setInput(prev => {
            const prefix = prev && !prev.endsWith(" ") ? prev + " " : prev;
            return prefix + transcript.trim();
          });
        } else if (transcript === null) {
          // Whisper unavailable — can't do anything with recorded audio
          // whisperAvailable state is already set to false
          console.warn("Whisper unavailable, recorded audio cannot be transcribed");
        }

        setListening(false);
        setRecordingSeconds(0);
      };

      // Collect data every second for reliability
      recorder.start(1000);
      setListening(true);
      setRecordingSeconds(0);

      // Start recording timer with 5-min limit
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(prev => {
          if (prev >= MAX_RECORDING_SECONDS - 1) {
            stopMediaRecording();
            return prev + 1;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.warn("MediaRecorder start error:", err);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        alert("Microphone access denied. Please allow mic access in your browser settings.");
      } else {
        alert("Could not access microphone. Please check your device settings.");
      }
      setListening(false);
      setRecordingSeconds(0);
    }
  }, [transcribeWithWhisper, stopMediaRecording]);

  // ── Fallback: Web Speech API recording (used when Whisper is unavailable) ──
  const startSpeechRecognition = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = "en-CA";
    recognition.continuous = true;
    recognition.interimResults = true;

    const existingInput = input;
    let finalTranscript = "";

    recognition.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          finalTranscript += e.results[i][0].transcript + " ";
        } else {
          interim += e.results[i][0].transcript;
        }
      }
      const prefix = existingInput ? existingInput + " " : "";
      setInput(prefix + finalTranscript + interim);
      setInterimTranscript(interim);
    };

    recognition.onend = () => {
      setListening(false);
      setRecordingSeconds(0);
      setInterimTranscript("");
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (finalTranscript.trim()) {
        const prefix = existingInput ? existingInput + " " : "";
        setInput(prefix + finalTranscript.trim());
      }
    };

    recognition.onerror = (e) => {
      console.warn("Speech recognition error:", e.error);
      setListening(false);
      setRecordingSeconds(0);
      setInterimTranscript("");
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (e.error === "not-allowed") {
        alert("Microphone access denied. Please allow mic access in your browser settings.");
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
    setRecordingSeconds(0);

    recordingTimerRef.current = setInterval(() => {
      setRecordingSeconds(prev => {
        if (prev >= MAX_RECORDING_SECONDS - 1) {
          recognition.stop();
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
  }, [input]);

  // ── Voice input: MediaRecorder (primary) or Web Speech API (fallback) ──
  const toggleListening = useCallback(() => {
    // Stop recording if already listening
    if (listening) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        stopMediaRecording();
      } else {
        recognitionRef.current?.stop();
        setListening(false);
        setRecordingSeconds(0);
        setInterimTranscript("");
        if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      }
      return;
    }

    // Primary path: MediaRecorder + Whisper (only if we haven't confirmed Whisper is down)
    if (mediaRecSupported && whisperAvailable !== false) {
      startMediaRecording();
      return;
    }

    // Fallback: Web Speech API
    if (speechSupported) {
      startSpeechRecognition();
      return;
    }

    alert("Voice input is not supported in this browser.");
  }, [listening, mediaRecSupported, whisperAvailable, speechSupported, startMediaRecording, startSpeechRecognition, stopMediaRecording]);

  // ── Job-note cards report here ──
  // The message is found by the draft's own id, never by its place in the list:
  // an answer can come back after the chat was closed and reopened, or after
  // another conversation was loaded, and then the place means something else.
  const recordNoteOutcome = useCallback((draftId, outcome) => {
    const withOutcome = (list) => list.map((m) => (
      (m.noteDrafts || []).some((d) => d.draftId === draftId)
        ? { ...m, noteOutcomes: { ...(m.noteOutcomes || {}), [draftId]: outcome } }
        : m
    ));
    // The next message is built from messagesRef, which otherwise only catches
    // up after the next paint. A message sent in that gap would tell Jai-Man
    // the card is still waiting, and would put the list back without the outcome.
    messagesRef.current = withOutcome(messagesRef.current || []);
    setMessages(withOutcome);
  }, []);

  // ── Send message (shared core for typed + hands-free voice turns) ──
  const sendMessageText = async (text, { viaVoiceConvo = false } = {}) => {
    if (readOnly) return; // view-as is a read-only preview; no writes as the impersonated user
    if (!text || loading) return;

    // Cancel any ongoing TTS when a new turn starts
    cancelSpeech();

    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    const newMessages = [...(messagesRef.current || []), userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      // A tap on Add that is still on its way takes a few seconds. This message
      // waits for its answer, so Jai-Man is told what really became of that
      // card and not a guess: a follow-up written on "it is being added" holds
      // only the new items, and if the add then fails the first part is lost.
      // The answer lands in messagesRef (recordNoteOutcome), so the list is
      // read again afterwards.
      let outgoing = newMessages;
      if (anyNoteBeingAdded(outgoing)) {
        const until = Date.now() + NOTE_ADD_WAIT_MS;
        while (Date.now() < until && anyNoteBeingAdded(messagesRef.current || [])) await new Promise((resolve) => setTimeout(resolve, 250));
        const latest = messagesRef.current || [];
        outgoing = latest.includes(userMsg) ? latest : [...latest, userMsg];
      }

      // The status lines are worked out from the whole list (a later draft can
      // replace an earlier card), so they are added before the greeting is dropped.
      const apiMessages = outgoing
        .map((m, i) => ({ role: m.role, content: withNoteStatus(m, i, outgoing) }))
        .filter((_, i) => !(i === 0 && outgoing[0].role === "assistant"));

      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "chat",
          messages: apiMessages,
          currentPath: resolvedCurrentPath,
          assessmentData: resolvedAssessmentData,
          userEmail,
          conversationId,
          // A job-note card is still waiting for its tap (see anyNoteDraftPending).
          ...(anyNoteDraftPending(outgoing) ? { noteDraftPending: true } : {}),
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.details || "API request failed");

      if (data.role) setUserRole(data.role);
      if (data.conversationId) setConversationId(data.conversationId);

      const rawContent = data.response || "Sorry, I couldn't get a response.";
      const fillData = parseAutoFill(rawContent);
      const cleanContent = stripAutoFill(rawContent);

      if (fillData && onAutoFill) {
        const fieldCount = Object.keys(fillData).length;
        onAutoFill(fillData);
        setFillBanner(`Jai-Man filled ${fieldCount} field${fieldCount > 1 ? "s" : ""}`);
        setTimeout(() => setFillBanner(null), 3500);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: cleanContent,
          timestamp: new Date().toISOString(),
          jobberActions: Array.isArray(data.jobberActions) ? data.jobberActions : [],
          // Job notes Jai-Man drafted this turn, one per job. They are NOT in
          // Jobber: the cards under this message are where the person adds
          // them (JobNoteCard), and noteOutcomes records what became of each.
          noteDrafts: Array.isArray(data.noteDrafts) ? data.noteDrafts.filter((d) => d && d.token && d.draftId) : [],
          noteOutcomes: {},
        },
      ]);
      const writeSummary = summarizeWriteActions(data.jobberActions || []);
      if (writeSummary) {
        setWriteStatusBanner(writeSummary);
        setTimeout(() => setWriteStatusBanner(null), 4000);
      }

      if (viaVoiceConvo && convoActiveRef.current) {
        // Hands-free loop: speak the reply, then afterSpeechEnd resumes listening
        const clean = stripMarkdownForTTS(cleanContent);
        if (clean) {
          setConvoStatusBoth("speaking");
          speakText(cleanContent, { force: true });
        } else {
          setConvoStatusBoth("listening");
        }
      } else if (voiceMode && !hasEscalationOffer(cleanContent)) {
        // Speak the response if voice mode is on
        speakText(cleanContent);
      }
    } catch (err) {
      console.error("Jai-Man error:", err);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Connection error: ${err.message}. Check your network and try again.`, timestamp: new Date().toISOString() },
      ]);
      if (viaVoiceConvo && convoActiveRef.current) setConvoStatusBoth("listening");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    if (listening) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(t => t.stop());
          audioStreamRef.current = null;
        }
      }
      recognitionRef.current?.stop();
      setListening(false);
      setRecordingSeconds(0);
      setInterimTranscript("");
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    }

    setInput("");
    await sendMessageText(text);
  };

  // ── Hands-free voice conversation engine ──
  // A single mic stream + WebAudio analyser runs for the whole conversation.
  // A 60ms VAD tick watches the mic level: speech starts a MediaRecorder
  // capture, a pause ends the turn (Whisper → send → spoken reply → listen
  // again). Sustained speech during Jai-Man's reply interrupts it (barge-in).
  const CONVO_SILENCE_END_MS = 1500; // pause length that ends your turn
  const CONVO_MIN_SPEECH_MS = 400;   // shorter blips (coughs, bumps) are ignored
  const CONVO_MAX_UTTERANCE_MS = 60000;
  const CONVO_BARGE_IN_MS = 450;     // sustained talking needed to interrupt a reply

  const convoVadTick = () => {
    const analyser = convoAnalyserRef.current;
    if (!analyser || !convoActiveRef.current) return;
    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const d = (buf[i] - 128) / 128;
      sum += d * d;
    }
    const rms = Math.sqrt(sum / buf.length);

    // Track the ambient noise floor so thresholds adapt to the environment
    const noise = convoNoiseFloorRef.current;
    if (rms < noise * 1.5) convoNoiseFloorRef.current = noise * 0.95 + rms * 0.05;
    const speechThresh = Math.max(noise * 3, 0.02);
    const bargeThresh = Math.max(noise * 5, 0.05); // higher bar while TTS is playing

    const now = Date.now();
    const status = convoStatusRef.current;

    if (status === "listening") {
      if (rms > speechThresh) beginConvoCapture(now);
    } else if (status === "capturing") {
      if (rms > speechThresh) convoLastVoiceRef.current = now;
      if (
        now - convoLastVoiceRef.current > CONVO_SILENCE_END_MS ||
        now - convoSpeechStartRef.current > CONVO_MAX_UTTERANCE_MS
      ) {
        endConvoCapture();
      }
    } else if (status === "speaking") {
      if (rms > bargeThresh) {
        convoBargeMsRef.current += 60;
        if (convoBargeMsRef.current >= CONVO_BARGE_IN_MS) {
          convoBargeMsRef.current = 0;
          cancelSpeech();
          beginConvoCapture(now); // user is already talking — capture it
        }
      } else {
        convoBargeMsRef.current = Math.max(0, convoBargeMsRef.current - 120);
      }
    }
  };

  const beginConvoCapture = (now) => {
    const stream = convoStreamRef.current;
    if (!stream) return;
    convoChunksRef.current = [];
    const mimeType = getAudioMimeType();
    let recorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    } catch (err) {
      console.warn("Convo recorder failed:", err);
      return;
    }
    convoRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) convoChunksRef.current.push(e.data);
    };
    recorder.onstop = () => convoCaptureStopRef.current();
    recorder.start(250);
    convoSpeechStartRef.current = now;
    convoLastVoiceRef.current = now;
    convoBargeMsRef.current = 0;
    setConvoStatusBoth("capturing");
  };

  const endConvoCapture = () => {
    setConvoStatusBoth("transcribing");
    const r = convoRecorderRef.current;
    if (r && r.state !== "inactive") r.stop(); // fires onstop → handleConvoCaptureStop
    else setConvoStatusBoth("listening");
  };

  const handleConvoCaptureStop = async () => {
    if (!convoActiveRef.current) return;
    const chunks = convoChunksRef.current;
    convoChunksRef.current = [];
    const recorder = convoRecorderRef.current;
    const usedMime = (recorder && recorder.mimeType) || getAudioMimeType() || "audio/webm";
    convoRecorderRef.current = null;

    const speechMs = convoLastVoiceRef.current - convoSpeechStartRef.current;
    if (!chunks.length || speechMs < CONVO_MIN_SPEECH_MS) {
      setConvoStatusBoth("listening"); // false trigger — keep listening
      return;
    }
    const blob = new Blob(chunks, { type: usedMime });
    const transcript = await transcribeWithWhisper(blob, usedMime);
    if (!convoActiveRef.current) return;
    const text = (transcript || "").trim();
    // Whisper hallucinates fragments on near-silence — require real content
    if (!text || text.replace(/[^a-z0-9]/gi, "").length < 2) {
      setConvoStatusBoth("listening");
      return;
    }
    setConvoStatusBoth("thinking");
    await sendMessageText(text, { viaVoiceConvo: true });
  };

  const startVoiceConvo = async () => {
    if (convoActiveRef.current || readOnly) return;
    cancelSpeech();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      convoStreamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      if (ctx.state === "suspended") await ctx.resume();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      convoAudioCtxRef.current = ctx;
      convoAnalyserRef.current = analyser;
      convoNoiseFloorRef.current = 0.01;
      convoActiveRef.current = true;
      setVoiceConvoActive(true);
      setConvoStatusBoth("listening");
      convoVadTimerRef.current = setInterval(() => convoTickRef.current(), 60);
    } catch (err) {
      console.warn("Voice conversation start failed:", err);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        alert("Microphone access denied. Please allow mic access in your browser settings.");
      } else {
        alert("Could not access the microphone for voice conversation.");
      }
    }
  };

  const stopVoiceConvo = useCallback(() => {
    convoActiveRef.current = false;
    setVoiceConvoActive(false);
    setConvoStatusBoth("idle");
    if (convoVadTimerRef.current) {
      clearInterval(convoVadTimerRef.current);
      convoVadTimerRef.current = null;
    }
    const r = convoRecorderRef.current;
    if (r && r.state !== "inactive") {
      r.onstop = null;
      try { r.stop(); } catch {}
    }
    convoRecorderRef.current = null;
    convoChunksRef.current = [];
    if (convoStreamRef.current) {
      convoStreamRef.current.getTracks().forEach(t => t.stop());
      convoStreamRef.current = null;
    }
    if (convoAudioCtxRef.current) {
      try { convoAudioCtxRef.current.close(); } catch {}
      convoAudioCtxRef.current = null;
    }
    convoAnalyserRef.current = null;
    cancelSpeech();
  }, [cancelSpeech, setConvoStatusBoth]);

  // Keep the timer/recorder callbacks + message snapshot on the latest render
  useEffect(() => {
    convoTickRef.current = convoVadTick;
    convoCaptureStopRef.current = handleConvoCaptureStop;
    messagesRef.current = messages;
  });

  // End the conversation if the widget is closed
  useEffect(() => {
    if (!open && convoActiveRef.current) stopVoiceConvo();
  }, [open, stopVoiceConvo]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyMessageText = async (text, key) => {
    const value = String(text || "");
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedMessageKey(key);
      setTimeout(() => {
        setCopiedMessageKey((curr) => (curr === key ? null : curr));
      }, 1400);
    } catch (err) {
      console.warn("Copy failed:", err);
    }
  };

  // ── Escalation handler ──
  const handleEscalate = async (aiMessage) => {
    if (escalating) return;
    setEscalating(true);
    const msgIndex = messages.findIndex(m => m.content === aiMessage);
    const precedingUserMsg = messages.slice(0, msgIndex).reverse().find(m => m.role === "user");

    try {
      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "escalate",
          userEmail,
          userMessage: precedingUserMsg?.content || "",
          jaiManAnalysis: aiMessage,
          context: { page: resolvedCurrentPath, assessmentData: resolvedAssessmentData, timestamp: new Date().toISOString() },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Escalation failed");

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Escalated to Dispatch. You'll be notified when it's resolved.", isEscalationConfirm: true, timestamp: new Date().toISOString() },
      ]);
    } catch (err) {
      console.error("Escalation error:", err);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Escalation failed: ${err.message}. Try again or contact support.`, timestamp: new Date().toISOString() },
      ]);
    } finally {
      setEscalating(false);
    }
  };

  // ── Format recording time ──
  const formatRecTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // ── Debounced history search ──
  const searchTimeoutRef = useRef(null);
  const handleHistorySearch = (val) => {
    setHistorySearch(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => loadHistory(), 400);
  };
  const handleMemorySearch = (val) => {
    setMemorySearch(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => loadMemories(), 400);
  };

  return (
    <>
      {fillBanner && <div style={styles.fillBanner}>{fillBanner}</div>}

      <button
        style={styles.fab}
        onClick={() => setOpen((o) => !o)}
        aria-label="Open Jai-Man assistant"
      >
        {open ? "\u2715" : "\u26A1"}
      </button>

      {open && (
        <div style={styles.overlay}>
          {/* ── Header ── */}
          <div style={styles.header}>
            <span style={styles.headerTitle}>{"\u26A1"} Jai-Man</span>
            {conversationTitle && !showHistory && !showMemories && (
              <span style={styles.convTitle}>{conversationTitle}</span>
            )}
            {showHistory && <span style={styles.convTitle}>History</span>}
            {showMemories && <span style={styles.convTitle}>Memory</span>}
            {!conversationTitle && !showHistory && !showMemories && <span style={styles.headerSub}>Brain of ElecBid</span>}
            <div style={{ flex: 1 }} />
            {/* Voice Mode toggle (AI voices play via Audio, so no speechSynthesis requirement) */}
            {(ttsSupported || openaiTTSAvailable !== false) && (
              <button
                style={{
                  ...styles.headerBtn,
                  background: voiceMode ? "#10b981" : "transparent",
                  color: voiceMode ? "#fff" : "#6b7280",
                  position: "relative",
                }}
                onClick={() => setVoiceMode(v => !v)}
                title={voiceMode ? "Voice mode ON — tap to turn off" : "Voice mode OFF — tap to turn on"}
              >
                {voiceMode ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <line x1="23" y1="9" x2="17" y2="15"/>
                    <line x1="17" y1="9" x2="23" y2="15"/>
                  </svg>
                )}
              </button>
            )}
            {/* History button */}
            <button
              style={{ ...styles.headerBtn, background: showHistory ? "#f59e0b" : "transparent", color: showHistory ? "#111827" : "#6b7280" }}
              onClick={() => {
                setShowHistory((h) => !h);
                setShowMemories(false);
              }}
              title="Conversation history"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </button>
            {isAdmin && (
              <button
                style={{ ...styles.headerBtn, background: showMemories ? "#10b981" : "transparent", color: showMemories ? "#111827" : "#6b7280" }}
                onClick={() => {
                  setShowMemories((m) => !m);
                  setShowHistory(false);
                }}
                title="Memory review"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2H6a2 2 0 0 0-2 2v16l5-3 5 3 5-3 5 3V9a2 2 0 0 0-2-2h-6"/>
                  <path d="M12 2v13"/>
                  <path d="M9 6h6"/>
                </svg>
              </button>
            )}
            {/* New Chat button */}
            <button style={styles.headerBtn} onClick={startNewChat} title="New chat">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
            <button style={styles.closeBtn} onClick={() => setOpen(false)}>{"\u2715"}</button>
          </div>

          {/* ── History Panel ── */}
          {showHistory ? (
            <div style={styles.historyPanel}>
              <div style={styles.historySearchRow}>
                <input
                  style={styles.historySearchInput}
                  placeholder="Search conversations..."
                  value={historySearch}
                  onChange={(e) => handleHistorySearch(e.target.value)}
                />
              </div>
              {historyLoading ? (
                <div style={styles.historyEmpty}>Loading...</div>
              ) : historyList.length === 0 ? (
                <div style={styles.historyEmpty}>No conversations yet</div>
              ) : (
                <div style={styles.historyList}>
                  {historyList.map((conv) => (
                    <button
                      key={conv.id}
                      style={{
                        ...styles.historyItem,
                        ...(conv.id === conversationId ? styles.historyItemActive : {}),
                      }}
                      onClick={() => loadConversation(conv.id)}
                    >
                      <div style={styles.historyItemTitle}>{conv.title}</div>
                      <div style={styles.historyItemMeta}>
                        {formatRelativeTime(conv.updated_at)} &middot; {conv.message_count} msgs
                      </div>
                      {conv.first_message && (
                        <div style={styles.historyItemPreview}>{conv.first_message}</div>
                      )}
                      {conv.tags?.length > 0 && (
                        <div style={styles.historyTags}>
                          {conv.tags.slice(0, 4).map((t, i) => (
                            <span key={i} style={styles.tag}>{t}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : showMemories ? (
            <div style={styles.historyPanel}>
              <div style={styles.historySearchRow}>
                <input
                  style={styles.historySearchInput}
                  placeholder="Search memories..."
                  value={memorySearch}
                  onChange={(e) => handleMemorySearch(e.target.value)}
                />
              </div>
              {memoryLoading ? (
                <div style={styles.historyEmpty}>Loading memories...</div>
              ) : memoryList.length === 0 ? (
                <div style={styles.historyEmpty}>No durable memories yet</div>
              ) : (
                <div style={styles.historyList}>
                  {memoryList.map((memory) => {
                    const draft = memoryDrafts[memory.id] || {};
                    const isEditing = editingMemoryId === memory.id;
                    return (
                      <div key={memory.id} style={styles.memoryCard}>
                        <div style={styles.memoryMetaRow}>
                          <span style={styles.memoryBadge}>{memory.memory_scope}</span>
                          <span style={styles.memoryBadgeMuted}>{memory.memory_type}</span>
                          <span style={styles.memoryMetaText}>
                            {memory.subject_label || memory.subject_key || "General"}
                          </span>
                        </div>
                        {isEditing ? (
                          <>
                            <input
                              style={styles.memoryInput}
                              value={draft.subject_label ?? ""}
                              onChange={(e) => updateMemoryDraft(memory.id, "subject_label", e.target.value)}
                              placeholder="Subject"
                            />
                            <textarea
                              style={styles.memoryTextarea}
                              value={draft.memory_text ?? ""}
                              onChange={(e) => updateMemoryDraft(memory.id, "memory_text", e.target.value)}
                              rows={3}
                            />
                            <input
                              style={styles.memoryInput}
                              value={draft.tags ?? ""}
                              onChange={(e) => updateMemoryDraft(memory.id, "tags", e.target.value)}
                              placeholder="tags, comma, separated"
                            />
                            <div style={styles.memoryNumberRow}>
                              <input
                                style={styles.memoryNumberInput}
                                value={draft.importance ?? "3"}
                                onChange={(e) => updateMemoryDraft(memory.id, "importance", e.target.value)}
                                placeholder="Importance"
                              />
                              <input
                                style={styles.memoryNumberInput}
                                value={draft.confidence ?? ""}
                                onChange={(e) => updateMemoryDraft(memory.id, "confidence", e.target.value)}
                                placeholder="Confidence"
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={styles.memoryText}>{memory.memory_text}</div>
                            <div style={styles.memoryMetaText}>
                              Updated {formatRelativeTime(memory.updated_at)} • importance {memory.importance} • confidence {memory.confidence ?? "n/a"}
                            </div>
                            {memory.tags?.length > 0 && (
                              <div style={styles.historyTags}>
                                {memory.tags.map((tag, index) => (
                                  <span key={index} style={styles.tag}>{tag}</span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                        <div style={styles.memoryActions}>
                          {isEditing ? (
                            <>
                              <button
                                style={styles.memoryActionBtnPrimary}
                                onClick={() => saveMemory(memory.id)}
                                disabled={savingMemoryId === memory.id}
                              >
                                {savingMemoryId === memory.id ? "Saving..." : "Save"}
                              </button>
                              <button style={styles.memoryActionBtn} onClick={cancelEditingMemory}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button style={styles.memoryActionBtn} onClick={() => startEditingMemory(memory)}>
                                Edit
                              </button>
                              <button
                                style={styles.memoryActionBtnDanger}
                                onClick={() => saveMemory(memory.id, false)}
                                disabled={savingMemoryId === memory.id}
                              >
                                Archive
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* ── Chat Messages ── */}
              <div style={styles.messages}>
                {messages.map((msg, i) => (
                  <div key={i}>
                    <div
                      style={{
                        ...styles.bubble,
                        ...(msg.role === "user" ? styles.userBubble : styles.aiBubble),
                        ...(msg.isEscalationConfirm ? styles.escalationConfirmBubble : {}),
                      }}
                    >
                      {msg.role === "assistant" ? renderMarkdown(msg.content) : msg.content}
                    </div>
                    {/* Timestamp */}
                    {msg.timestamp && (
                      <div style={{
                        ...styles.timestamp,
                        textAlign: msg.role === "user" ? "right" : "left",
                      }}>
                        {formatTimestamp(msg.timestamp)}
                      </div>
                    )}
                    <div style={{
                      ...styles.copyRow,
                      justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                    }}>
                      <button
                        style={styles.copyBtn}
                        onClick={() => copyMessageText(msg.content, `${i}-${msg.timestamp || ""}`)}
                        title="Copy message"
                      >
                        {copiedMessageKey === `${i}-${msg.timestamp || ""}` ? "Copied" : "Copy"}
                      </button>
                      {msg.role === "assistant" && (
                        <button
                          style={styles.copyBtn}
                          onClick={() => {
                            if (isSpeaking && speakingMsgRef.current === msg.content) {
                              cancelSpeech();
                            } else {
                              speakText(msg.content, { force: true });
                            }
                          }}
                          title="Read aloud"
                        >
                          {isSpeaking && speakingMsgRef.current === msg.content ? "■ Stop" : "🔊 Speak"}
                        </button>
                      )}
                    </div>
                    {msg.role === "assistant" && Array.isArray(msg.jobberActions) && msg.jobberActions.length > 0 && (
                      <div style={styles.jobberActionRow}>
                        {msg.jobberActions.map((action, idx) => (
                          <span
                            key={`${action.action || "jobber"}-${idx}`}
                            style={{
                              ...styles.jobberActionPill,
                              ...(action.ok ? styles.jobberActionPillOk : styles.jobberActionPillError),
                            }}
                            title={action.ok ? formatJobberActionLabel(action.action) : (action.error || "Jobber action failed")}
                          >
                            {formatJobberActionLabel(action.action)}{action.ok ? "" : " Error"}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Drafted job notes: one card each, with the "Add to job" button */}
                    {msg.role === "assistant" && Array.isArray(msg.noteDrafts) && msg.noteDrafts.map((d) => (
                      <JobNoteCard
                        key={d.draftId}
                        draft={d}
                        readOnly={readOnly}
                        // A later reply drafted this job again, or replaced its job number.
                        superseded={replacedByLaterDraft(messages, i, d)}
                        // Jai-Man is answering, maybe with a correction to this card: Add waits.
                        busy={loading}
                        onOutcome={recordNoteOutcome}
                      />
                    ))}
                    {/* Escalation button */}
                    {!readOnly && msg.role === "assistant" && userRole === "admin" && hasEscalationOffer(msg.content) && !msg.isEscalationConfirm && (
                      <button
                        style={{ ...styles.escalateBtn, opacity: escalating ? 0.6 : 1 }}
                        onClick={() => handleEscalate(msg.content)}
                        disabled={escalating}
                      >
                        {escalating ? "Escalating..." : "Escalate to Dispatch"}
                      </button>
                    )}
                  </div>
                ))}
                {loading && (
                  <div style={{ ...styles.bubble, ...styles.aiBubble, ...styles.thinkingBubble }}>
                    <span style={styles.thinkingDots}>
                      <span style={styles.dot1}>.</span>
                      <span style={styles.dot2}>.</span>
                      <span style={styles.dot3}>.</span>
                    </span>
                    {" "}Jai-Man is thinking...
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* ── Speaking Indicator + Stop (hidden in convo mode — convo bar covers it) ── */}
              {isSpeaking && !voiceConvoActive && (
                <div style={styles.speakingBar}>
                  <div style={styles.speakingWave}>
                    <span style={styles.waveBar1} /><span style={styles.waveBar2} /><span style={styles.waveBar3} /><span style={styles.waveBar4} /><span style={styles.waveBar5} />
                  </div>
                  <span style={styles.speakingLabel}>Jai-Man is speaking...</span>
                  <button style={styles.stopSpeakBtn} onClick={cancelSpeech}>Stop</button>
                </div>
              )}

              {/* ── Voice Mode Controls (also shown during a live conversation for voice switching) ── */}
              {(voiceMode || voiceConvoActive) && (
                <div style={styles.voiceModeBar}>
                  <span style={styles.voiceModeLabel}>Voice</span>
                  <select
                    style={styles.voiceSelect}
                    value={selectedVoice}
                    onChange={(e) => {
                      cancelSpeech();
                      setSelectedVoice(e.target.value);
                      // Mid-reply voice swap in a live convo: resume listening
                      if (convoActiveRef.current && convoStatusRef.current === "speaking") {
                        setConvoStatusBoth("listening");
                      }
                    }}
                  >
                    {openaiTTSAvailable !== false && (
                      <optgroup label="Jai-Man AI voices">
                        {OPENAI_VOICES.map(v => (
                          <option key={v.id} value={`openai:${v.id}`}>{v.label}</option>
                        ))}
                      </optgroup>
                    )}
                    {browserVoices.length > 0 && (
                      <optgroup label="Device voices (free)">
                        {browserVoices.map(v => (
                          <option key={v.name} value={`browser:${v.name}`}>
                            {v.name.replace(/^Microsoft |^Google /, "")}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {!voiceConvoActive && (
                    <button
                      style={styles.voiceTestBtn}
                      onClick={() => speakText("Hey, it's Jai-Man. This is how I'll sound when I talk back.", { force: true })}
                      title="Preview this voice"
                    >
                      Test
                    </button>
                  )}
                  {!voiceConvoActive && (
                    <label style={styles.autoListenLabel}>
                      <input
                        type="checkbox"
                        checked={autoListen}
                        onChange={(e) => setAutoListen(e.target.checked)}
                        style={styles.autoListenCheck}
                      />
                      Auto-listen
                    </label>
                  )}
                </div>
              )}

              {/* ── AI voice fallback notice ── */}
              {voiceMode && openaiTTSAvailable === false && (
                <div style={{ padding: "4px 12px", fontSize: 11, color: "#f59e0b", textAlign: "center" }}>
                  AI voices unavailable — using your device voice
                </div>
              )}

              {/* ── Transcribing indicator (convo bar shows its own) ── */}
              {transcribing && !voiceConvoActive && (
                <div style={styles.recordingBar}>
                  <div style={{ ...styles.recordingPulse, background: "#f59e0b" }} />
                  <span style={styles.recordingTime}>Transcribing...</span>
                </div>
              )}

              {/* ── Whisper fallback notice ── */}
              {whisperAvailable === false && (
                <div style={{ padding: "4px 12px", fontSize: 11, color: "#f59e0b", textAlign: "center" }}>
                  Whisper unavailable — using browser speech recognition (may auto-stop on pauses)
                </div>
              )}

              {/* ── Voice Recording Bar ── */}
              {listening && (
                <div style={styles.recordingBar}>
                  <div style={styles.recordingPulse} />
                  <span style={styles.recordingTime}>{formatRecTime(recordingSeconds)}</span>
                  <span style={styles.recordingMax}>/ {formatRecTime(MAX_RECORDING_SECONDS)}</span>
                  {interimTranscript && (
                    <div style={styles.interimPreview}>{interimTranscript}</div>
                  )}
                  <button style={styles.stopBtn} onClick={toggleListening}>
                    Stop
                  </button>
                </div>
              )}

              {/* ── Input Row ── */}
              {writeStatusBanner && (
                <div
                  style={{
                    ...styles.writeStatusBanner,
                    ...(writeStatusBanner.kind === "success"
                      ? styles.writeStatusSuccess
                      : writeStatusBanner.kind === "warn"
                        ? styles.writeStatusWarn
                        : styles.writeStatusError),
                  }}
                >
                  {writeStatusBanner.text}
                </div>
              )}
              {readOnly ? (
                <div style={styles.hint}>
                  Read-only preview while viewing as another user — exit view-as to chat with Jai-Man.
                </div>
              ) : voiceConvoActive ? (
                /* ── Live voice conversation bar ── */
                <div style={styles.convoBar}>
                  <div
                    style={{
                      ...styles.convoOrb,
                      background:
                        convoStatus === "speaking" ? "#10b981"
                        : convoStatus === "transcribing" || convoStatus === "thinking" ? "#f59e0b"
                        : "#ef4444",
                      animation:
                        convoStatus === "transcribing" || convoStatus === "thinking"
                          ? "none"
                          : "jm-rec-pulse 1.2s ease-in-out infinite",
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.convoStatusLabel}>
                      {convoStatus === "capturing" ? "Listening..."
                        : convoStatus === "listening" ? "Listening — just talk"
                        : convoStatus === "transcribing" ? "Got it — transcribing..."
                        : convoStatus === "thinking" ? "Jai-Man is thinking..."
                        : convoStatus === "speaking" ? "Speaking — talk to interrupt"
                        : "Voice conversation"}
                    </div>
                    <div style={styles.convoSub}>
                      Hands-free: talk, pause, and Jai-Man answers. Tap End to finish.
                    </div>
                  </div>
                  {convoStatus === "speaking" && (
                    <button
                      style={styles.stopSpeakBtn}
                      onClick={() => {
                        cancelSpeech();
                        setConvoStatusBoth("listening");
                      }}
                    >
                      Skip
                    </button>
                  )}
                  <button style={styles.convoEndBtn} onClick={stopVoiceConvo}>End</button>
                </div>
              ) : (
              <div style={styles.inputRow}>
                <textarea
                  ref={textareaRef}
                  style={styles.textarea}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything or give walking notes..."
                  rows={2}
                />
                {(mediaRecSupported || speechSupported) && !listening && !transcribing && (
                  <button
                    style={styles.micBtn}
                    onClick={toggleListening}
                    aria-label="Tap to record"
                    title="Dictate into the text box"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e5e7eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                      <line x1="12" y1="19" x2="12" y2="23"/>
                      <line x1="8" y1="23" x2="16" y2="23"/>
                    </svg>
                  </button>
                )}
                {mediaRecSupported && whisperAvailable !== false && !listening && !transcribing && (
                  <button
                    style={styles.talkBtn}
                    onClick={startVoiceConvo}
                    aria-label="Start voice conversation"
                    title="Talk live with Jai-Man — hands-free"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#052e26" strokeWidth="2.4" strokeLinecap="round">
                      <line x1="4" y1="10" x2="4" y2="14"/>
                      <line x1="9" y1="6" x2="9" y2="18"/>
                      <line x1="14" y1="9" x2="14" y2="15"/>
                      <line x1="19" y1="11" x2="19" y2="13"/>
                    </svg>
                  </button>
                )}
                <button
                  style={{ ...styles.sendBtn, opacity: loading || !input.trim() ? 0.5 : 1 }}
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                >
                  {"\u27A4"}
                </button>
              </div>
              )}
              {!readOnly && !listening && !voiceConvoActive && (
                <p style={styles.hint}>
                  {mediaRecSupported && whisperAvailable !== false
                    ? "Waveform = talk live | Mic = dictate | Enter = send"
                    : (mediaRecSupported || speechSupported) ? "Tap mic to record | Enter to send" : "Enter to send"}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <style>{ANIMATION_CSS}</style>
    </>
  );
}

const ANIMATION_CSS = `
  @keyframes jm-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.6); opacity: 0.4; }
  }
  @keyframes jm-dot1 { 0%, 20% { opacity: 0; } 30%, 100% { opacity: 1; } }
  @keyframes jm-dot2 { 0%, 40% { opacity: 0; } 50%, 100% { opacity: 1; } }
  @keyframes jm-dot3 { 0%, 60% { opacity: 0; } 70%, 100% { opacity: 1; } }
  @keyframes jm-rec-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  @keyframes jm-wave {
    0%, 100% { transform: scaleY(0.4); }
    50% { transform: scaleY(1); }
  }
`;

const styles = {
  fab: {
    position: "fixed",
    bottom: 88,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #a5ce3a, #7eb821)",
    border: "none",
    fontSize: 24,
    cursor: "pointer",
    zIndex: 1000,
    boxShadow: "0 4px 16px rgba(165,206,58,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#111827",
    fontWeight: 700,
  },
  overlay: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    height: "80vh",
    maxHeight: 650,
    background: "#111827",
    borderTop: "2px solid #a5ce3a",
    borderRadius: "20px 20px 0 0",
    display: "flex",
    flexDirection: "column",
    zIndex: 999,
    boxShadow: "0 -8px 32px rgba(0,0,0,0.6)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "12px 12px 8px",
    borderBottom: "1px solid #1f2937",
    gap: 6,
    flexShrink: 0,
  },
  headerTitle: {
    color: "#f59e0b",
    fontWeight: 700,
    fontSize: 17,
    flexShrink: 0,
  },
  headerSub: {
    color: "#6b7280",
    fontSize: 12,
  },
  convTitle: {
    color: "#9ca3af",
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 160,
  },
  headerBtn: {
    background: "transparent",
    border: "none",
    color: "#6b7280",
    cursor: "pointer",
    padding: "4px 6px",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#6b7280",
    fontSize: 18,
    cursor: "pointer",
    padding: "0 4px",
    flexShrink: 0,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "10px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    WebkitOverflowScrolling: "touch",
  },
  bubble: {
    maxWidth: "88%",
    padding: "10px 14px",
    borderRadius: 14,
    fontSize: 14,
    lineHeight: 1.5,
    userSelect: "text",
    WebkitUserSelect: "text",
  },
  aiBubble: {
    background: "#1f2937",
    color: "#e5e7eb",
    alignSelf: "flex-start",
    borderBottomLeftRadius: 4,
  },
  userBubble: {
    background: "#f59e0b",
    color: "#111827",
    alignSelf: "flex-end",
    borderBottomRightRadius: 4,
    fontWeight: 500,
    whiteSpace: "pre-wrap",
  },
  timestamp: {
    fontSize: 10,
    color: "#4b5563",
    padding: "1px 4px",
    marginTop: 1,
  },
  copyRow: {
    display: "flex",
    marginTop: 2,
  },
  copyBtn: {
    background: "transparent",
    border: "1px solid #374151",
    color: "#9ca3af",
    borderRadius: 8,
    fontSize: 11,
    lineHeight: 1,
    padding: "5px 8px",
    cursor: "pointer",
  },
  jobberActionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    margin: "4px 0 0 0",
  },
  jobberActionPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.2,
    textTransform: "uppercase",
    borderRadius: 999,
    padding: "4px 8px",
  },
  jobberActionPillOk: {
    background: "rgba(16,185,129,0.14)",
    color: "#6ee7b7",
    border: "1px solid rgba(16,185,129,0.28)",
  },
  jobberActionPillError: {
    background: "rgba(239,68,68,0.14)",
    color: "#fca5a5",
    border: "1px solid rgba(239,68,68,0.28)",
  },
  thinkingBubble: {
    opacity: 0.7,
    fontStyle: "italic",
  },
  thinkingDots: {
    display: "inline-block",
    letterSpacing: 2,
  },
  dot1: { animation: "jm-dot1 1.4s infinite" },
  dot2: { animation: "jm-dot2 1.4s infinite" },
  dot3: { animation: "jm-dot3 1.4s infinite" },
  inputRow: {
    display: "flex",
    gap: 8,
    padding: "8px 12px 4px",
    alignItems: "flex-end",
    flexShrink: 0,
  },
  textarea: {
    flex: 1,
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: 10,
    color: "#e5e7eb",
    padding: "10px 12px",
    fontSize: 14,
    resize: "none",
    outline: "none",
    fontFamily: "inherit",
    WebkitAppearance: "none",
    userSelect: "text",
    WebkitUserSelect: "text",
  },
  micBtn: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: "none",
    background: "#374151",
    color: "#e5e7eb",
    fontSize: 20,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.15s",
  },
  talkBtn: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: "none",
    background: "#10b981",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.15s",
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: "#f59e0b",
    border: "none",
    color: "#111827",
    fontSize: 18,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontWeight: 700,
    transition: "opacity 0.15s",
  },
  hint: {
    color: "#4b5563",
    fontSize: 11,
    textAlign: "center",
    padding: "2px 0 10px",
    margin: 0,
    flexShrink: 0,
  },
  fillBanner: {
    position: "fixed",
    top: 20,
    left: "50%",
    transform: "translateX(-50%)",
    background: "#10b981",
    color: "#fff",
    padding: "10px 20px",
    borderRadius: 24,
    fontSize: 14,
    fontWeight: 600,
    zIndex: 1100,
    boxShadow: "0 4px 16px rgba(16,185,129,0.4)",
    pointerEvents: "none",
  },
  escalateBtn: {
    display: "block",
    margin: "6px 0 0 0",
    padding: "8px 16px",
    background: "linear-gradient(135deg, #10b981, #059669)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(16,185,129,0.3)",
    transition: "opacity 0.15s",
    alignSelf: "flex-start",
  },
  escalationConfirmBubble: {
    background: "#065f46",
    borderLeft: "3px solid #10b981",
  },
  writeStatusBanner: {
    margin: "6px 12px 0",
    borderRadius: 10,
    padding: "8px 10px",
    fontSize: 12,
    fontWeight: 600,
    border: "1px solid transparent",
  },
  writeStatusSuccess: {
    background: "rgba(16,185,129,0.16)",
    color: "#6ee7b7",
    borderColor: "rgba(16,185,129,0.35)",
  },
  writeStatusWarn: {
    background: "rgba(245,158,11,0.16)",
    color: "#fcd34d",
    borderColor: "rgba(245,158,11,0.35)",
  },
  writeStatusError: {
    background: "rgba(239,68,68,0.14)",
    color: "#fca5a5",
    borderColor: "rgba(239,68,68,0.32)",
  },

  // ── Recording bar ──
  recordingBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    background: "#1c1c1c",
    borderTop: "1px solid #374151",
    flexShrink: 0,
    flexWrap: "wrap",
  },
  recordingPulse: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#ef4444",
    animation: "jm-rec-pulse 1s ease-in-out infinite",
    flexShrink: 0,
  },
  recordingTime: {
    color: "#ef4444",
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "monospace",
  },
  recordingMax: {
    color: "#6b7280",
    fontSize: 12,
  },
  interimPreview: {
    flex: 1,
    color: "#9ca3af",
    fontSize: 12,
    fontStyle: "italic",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 80,
  },
  stopBtn: {
    padding: "6px 16px",
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
  },

  // ── Speaking bar ──
  speakingBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 14px",
    background: "#0d1117",
    borderTop: "1px solid #1f2937",
    flexShrink: 0,
  },
  speakingWave: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    height: 18,
  },
  waveBar1: { display: "inline-block", width: 3, height: 8, background: "#10b981", borderRadius: 2, animation: "jm-wave 0.8s ease-in-out infinite" },
  waveBar2: { display: "inline-block", width: 3, height: 14, background: "#10b981", borderRadius: 2, animation: "jm-wave 0.8s ease-in-out 0.1s infinite" },
  waveBar3: { display: "inline-block", width: 3, height: 18, background: "#10b981", borderRadius: 2, animation: "jm-wave 0.8s ease-in-out 0.2s infinite" },
  waveBar4: { display: "inline-block", width: 3, height: 14, background: "#10b981", borderRadius: 2, animation: "jm-wave 0.8s ease-in-out 0.3s infinite" },
  waveBar5: { display: "inline-block", width: 3, height: 8, background: "#10b981", borderRadius: 2, animation: "jm-wave 0.8s ease-in-out 0.4s infinite" },
  speakingLabel: {
    color: "#10b981",
    fontSize: 13,
    fontWeight: 600,
    flex: 1,
  },
  stopSpeakBtn: {
    padding: "5px 14px",
    background: "#374151",
    color: "#e5e7eb",
    border: "1px solid #4b5563",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
  },

  // ── Live voice conversation bar ──
  convoBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: "#0d1117",
    borderTop: "1px solid #10b981",
    flexShrink: 0,
  },
  convoOrb: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    flexShrink: 0,
  },
  convoStatusLabel: {
    color: "#e5e7eb",
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  convoSub: {
    color: "#6b7280",
    fontSize: 11,
    marginTop: 2,
  },
  convoEndBtn: {
    padding: "8px 16px",
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
  },

  // ── Voice mode bar ──
  voiceModeBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 14px",
    background: "#0d1117",
    borderTop: "1px solid #1f2937",
    flexShrink: 0,
    flexWrap: "wrap",
  },
  voiceModeLabel: {
    color: "#10b981",
    fontSize: 11,
    fontWeight: 600,
    flexShrink: 0,
  },
  voiceSelect: {
    flex: 1,
    minWidth: 120,
    maxWidth: 260,
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: 8,
    color: "#e5e7eb",
    padding: "5px 8px",
    fontSize: 12,
    outline: "none",
    fontFamily: "inherit",
  },
  voiceTestBtn: {
    padding: "5px 12px",
    background: "#374151",
    color: "#e5e7eb",
    border: "1px solid #4b5563",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
  },
  autoListenLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: "#9ca3af",
    fontSize: 11,
    cursor: "pointer",
    marginLeft: "auto",
    flexShrink: 0,
  },
  autoListenCheck: {
    accentColor: "#10b981",
    cursor: "pointer",
  },

  // ── History panel ──
  historyPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  historySearchRow: {
    padding: "10px 14px",
    flexShrink: 0,
  },
  historySearchInput: {
    width: "100%",
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: 10,
    color: "#e5e7eb",
    padding: "10px 12px",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  historyList: {
    flex: 1,
    overflowY: "auto",
    padding: "0 14px 14px",
    WebkitOverflowScrolling: "touch",
  },
  historyEmpty: {
    color: "#6b7280",
    fontSize: 14,
    textAlign: "center",
    padding: 40,
  },
  historyItem: {
    display: "block",
    width: "100%",
    textAlign: "left",
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: 12,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "border-color 0.15s",
    color: "#e5e7eb",
    fontFamily: "inherit",
  },
  historyItemActive: {
    borderColor: "#f59e0b",
  },
  historyItemTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 3,
    color: "#f9fafb",
  },
  historyItemMeta: {
    fontSize: 11,
    color: "#6b7280",
    marginBottom: 4,
  },
  historyItemPreview: {
    fontSize: 12,
    color: "#9ca3af",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: 4,
  },
  historyTags: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
  },
  tag: {
    fontSize: 10,
    background: "#374151",
    color: "#d1d5db",
    padding: "2px 6px",
    borderRadius: 6,
  },
  memoryCard: {
    background: "#1f2937",
    border: "1px solid #374151",
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  memoryMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  memoryBadge: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    background: "#10b981",
    color: "#06281e",
    padding: "2px 7px",
    borderRadius: 999,
  },
  memoryBadgeMuted: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    background: "#334155",
    color: "#cbd5e1",
    padding: "2px 7px",
    borderRadius: 999,
  },
  memoryMetaText: {
    color: "#94a3b8",
    fontSize: 11,
    lineHeight: 1.4,
  },
  memoryText: {
    color: "#f3f4f6",
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
  memoryInput: {
    width: "100%",
    background: "#111827",
    border: "1px solid #374151",
    borderRadius: 8,
    color: "#e5e7eb",
    padding: "8px 10px",
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  memoryTextarea: {
    width: "100%",
    minHeight: 84,
    background: "#111827",
    border: "1px solid #374151",
    borderRadius: 8,
    color: "#e5e7eb",
    padding: "8px 10px",
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
    resize: "vertical",
    boxSizing: "border-box",
  },
  memoryNumberRow: {
    display: "flex",
    gap: 8,
  },
  memoryNumberInput: {
    flex: 1,
    minWidth: 0,
    background: "#111827",
    border: "1px solid #374151",
    borderRadius: 8,
    color: "#e5e7eb",
    padding: "8px 10px",
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
  },
  memoryActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  memoryActionBtn: {
    border: "none",
    borderRadius: 8,
    padding: "7px 12px",
    background: "#334155",
    color: "#e5e7eb",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  memoryActionBtnPrimary: {
    border: "none",
    borderRadius: 8,
    padding: "7px 12px",
    background: "#10b981",
    color: "#052e26",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  memoryActionBtnDanger: {
    border: "none",
    borderRadius: 8,
    padding: "7px 12px",
    background: "#7f1d1d",
    color: "#fee2e2",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
};
